/* Hover a query badge / icon → whole thread (type, products, messages, follow-up, outcome) + quick reply. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Paperclip, Send } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

const cache = {};
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const KIND = { open: "Opened", note: "Note", outcome: "Outcome", processed: "Processed", reopen: "Reopened", partner: "Partner", followup: "Follow-up" };
const myId = () => { try { return JSON.parse(localStorage.getItem("user") || "{}").id || ""; } catch { return ""; } };

// Unread = written by someone else after my last reply on this query.
export const isUnread = (q, e) => {
  const me = myId();
  if (!me || e.by_id === me) return false;
  const seen = (q.seen_by || {})[me];
  return !seen || String(e.at) > String(seen);
};

const Entry = ({ q, e, i }) => {
  const isNew = isUnread(q, e);
  const partner = e.from === "partner" || e.kind === "partner";
  return (
    <div className={`rounded px-2 py-1 ${isNew ? "bg-amber-100 ring-1 ring-amber-300" : partner ? "ml-6 bg-teal-50" : "mr-6 bg-slate-100"}`}
      data-testid={`query-hover-entry-${i}`}>
      <div className="text-[10px] text-slate-500">
        {isNew && <span className="mr-1 rounded bg-amber-500 px-1 text-[9px] font-bold text-white" data-testid={`query-hover-new-${i}`}>NEW</span>}
        <b className="text-slate-700">{partner ? "Partner" : e.by_name || "Team"}</b> · {KIND[e.kind] || e.kind} · {when(e.at)}
      </div>
      {e.text && <div className="whitespace-pre-wrap text-[11px] text-slate-800">{e.text}</div>}
      {e.files?.length > 0 && <div className="text-[10px] text-slate-500"><Paperclip className="inline h-3 w-3" /> {e.files.length} attachment(s)</div>}
    </div>
  );
};

const QuickReply = ({ q, onSent }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/order-register/queries/${q.id}/note`, { text: text.trim() }, { headers: getAuthHeader() });
      setText(""); toast.success("Reply added"); onSent(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Reply failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-end gap-1 border-t pt-1.5">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Quick reply…"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
        className="min-w-0 flex-1 resize-none rounded border px-2 py-1 text-[11px]" data-testid="query-hover-reply-input" />
      <button type="button" onClick={send} disabled={busy || !text.trim()}
        className="inline-flex h-7 items-center rounded bg-orange-600 px-2 text-white disabled:opacity-50" data-testid="query-hover-reply-send">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}</button>
    </div>
  );
};

export const QueryThreadCard = ({ q, older = 0, onSent }) => (
  <div className="space-y-1.5 text-xs">
    <div className="flex flex-wrap items-center gap-1.5">
      <b className="text-slate-800">{q.type}</b>
      <span className={`rounded px-1 text-[10px] font-semibold ${q.status === "solved" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>{q.status}</span>
      {q.from_partner && <span className="rounded bg-teal-100 px-1 text-[10px] text-teal-700">Partner query</span>}
    </div>
    {q.products?.length > 0 && <div className="text-[10px] text-slate-500">Products: {q.products.join(", ")}</div>}
    <div className="max-h-64 space-y-1 overflow-y-auto pr-1" data-testid="query-hover-thread">
      {(q.entries || []).map((e, i) => <Entry key={i} q={q} e={e} i={i} />)}
    </div>
    {q.next_followup_at && q.status !== "solved" && <div className="text-[10px] text-amber-700">Follow-up: {when(q.next_followup_at)}</div>}
    {q.outcome && <div className="text-[11px] font-semibold text-emerald-700">Outcome: {q.outcome}</div>}
    {older > 0 && <div className="text-[10px] text-slate-400">+{older} older quer{older === 1 ? "y" : "ies"} — click to open</div>}
    {onSent && q.status === "open" && <QuickReply q={q} onSent={onSent} />}
  </div>
);

const fetchThread = async (url, key) => {
  const c = cache[key];
  if (c && Date.now() - c.at < 30000) return c.d;
  const r = await axios.get(url, { headers: getAuthHeader() });
  cache[key] = { at: Date.now(), d: r.data };
  return r.data;
};

const HoverShell = ({ id, load, children, older, side = "right" }) => {
  const [d, setD] = useState(null);
  const open = (o) => { if (o) load().then(setD).catch(() => setD({ current: null })); };
  const sent = (q) => { Object.keys(cache).forEach(k => delete cache[k]); setD(x => ({ ...x, current: q })); };
  return (
    <HoverCard openDelay={250} closeDelay={200} onOpenChange={open}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side={side} align="start" className="w-80 p-3" data-testid={`query-hover-${id}`}>
        {!d ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          : d.current ? <QueryThreadCard q={d.current} older={older ? older(d) : 0} onSent={sent} />
          : <p className="text-xs text-slate-400">No query on this parcel yet.</p>}
      </HoverCardContent>
    </HoverCard>
  );
};

export const QueryHover = ({ rowId, children }) => (
  <HoverShell id={rowId} load={() => fetchThread(`${API}/order-register/queries/by-row/${rowId}`, `row:${rowId}`)}
    older={(d) => Math.max(0, (d.history || []).length - 1)}>{children}</HoverShell>
);

export const QueryHoverById = ({ qid, children, side }) => (
  <HoverShell id={qid} side={side} load={() => fetchThread(`${API}/order-register/queries/one/${qid}`, `q:${qid}`).then(q => ({ current: q }))}>
    {children}</HoverShell>
);
